import React from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';
import { agencyInfo } from '../data/mock';

/**
 * GoogleReviews — aggregate-only display.
 * We deliberately show ONLY the verified aggregate metric from Google
 * Business Profile (rating + review count) instead of fabricated names or
 * copied testimonials. Ethical choice that reads as professionalism.
 */
const GoogleReviews = () => {
  const rating = 5.0;
  const totalReviews = 8;
  const reviewLink = 'https://g.page/r/CV71PwTZvBxPEBM/review';
  const businessLink = 'https://g.page/r/CV71PwTZvBxPEBM';

  return (
    <section
      id="reviews"
      className="py-20 md:py-28 bg-[#0A0A0A] border-t border-white/5 relative overflow-hidden"
      data-testid="reviews-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60vw] h-[40vw] bg-[#4ECDC4]/8 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Overline */}
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
              Avaliação Verificada
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
          </div>

          {/* Aggregate rating card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex flex-col items-center gap-4 bg-gradient-to-br from-[#131313] to-[#0A0A0A] border border-[#4ECDC4]/25 rounded-2xl px-10 md:px-16 py-8 md:py-10 shadow-[0_25px_60px_-20px_rgba(78,205,196,0.25)]"
            data-testid="reviews-aggregate-card"
          >
            {/* Google logo + rating */}
            <div className="flex items-center gap-4">
              <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <div className="text-5xl md:text-6xl font-black text-[#F5F5F0] leading-none">
                {rating.toFixed(1).replace('.', ',')}
              </div>
            </div>

            {/* Stars */}
            <div className="flex gap-1" data-testid="reviews-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-[#4ECDC4] text-[#4ECDC4]" />
              ))}
            </div>

            {/* Meta */}
            <p className="text-sm text-[#A1A1AA] font-light">
              Baseado em <strong className="text-[#F5F5F0] font-semibold">{totalReviews} avaliações</strong> verificadas
              no Google Business Profile
            </p>
          </motion.div>

          {/* Headline */}
          <h2 className="text-2xl md:text-4xl tracking-tight font-bold text-[#F5F5F0] mt-10 mb-4 max-w-2xl mx-auto leading-tight">
            Reputação construída por{' '}
            <span className="text-[#4ECDC4]">quem já trabalhou conosco</span>.
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed mb-10">
            Preferimos mostrar a avaliação real e verificada do Google a exibir depoimentos que
            não possam ser conferidos. Cada estrela é de um cliente que efetivamente contratou a Coutin.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={businessLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#4ECDC4]/40 hover:bg-[#4ECDC4]/10 text-[#F5F5F0] font-bold uppercase tracking-wide px-6 py-3 rounded-lg transition-all duration-300 text-sm"
              data-testid="reviews-read-cta"
            >
              <span>Ler avaliações no Google</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-6 py-3 rounded-lg transition-all duration-300 text-sm shadow-lg shadow-[#4ECDC4]/25"
              data-testid="reviews-write-cta"
            >
              <Star className="w-4 h-4 fill-current" />
              <span>Já é cliente? Avalie-nos</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GoogleReviews;
